import { ServiceCapabilityIntent, ServiceLocationInfo } from '../types/firestore';
import {
  formatServiceCapabilityIntents,
  providerCapabilityCategoryById,
  providerCapabilityObjectById,
} from './serviceCapabilities';
import { formatServiceHourlyRateAmount, getPublicServiceHourlyRate } from './servicePricing';
import { getSmartSearchSelectionByParams, type SmartSearchSelection } from './smartSearch';

export type ServiceDiscoverySort = 'name' | 'rating' | 'reviews';
export type ServiceDiscoveryIssueType = ServiceCapabilityIntent | null;
export const DEFAULT_DISCOVERY_SORT: ServiceDiscoverySort = 'rating';
export const DEFAULT_DISCOVERY_INCLUDE_UNPRICED = true;
export const DISCOVERY_HOURLY_RATE_PRESETS = [100, 150, 200, 300] as const;
export type ServiceDiscoveryIncludeUnpricedParamMode = 'implicit' | 'explicit-on' | 'explicit-off';

export interface ServiceDiscoveryFilters {
  query: string;
  city: string;
  sortBy: ServiceDiscoverySort;
  hourlyRateMax: number | null;
  includeUnpriced: boolean;
  includeUnpricedParamMode: ServiceDiscoveryIncludeUnpricedParamMode;
  issueType: ServiceDiscoveryIssueType;
  smartSelection: SmartSearchSelection | null;
}

type ServiceDiscoverySearchParamUpdate = Partial<ServiceDiscoveryFilters>;

export const isRenderableServicePosition = (
  position: [number, number] | undefined
): position is [number, number] => {
  if (!position) return false;
  const [lat, lng] = position;
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};

const normalize = (value?: string): string => (value || '').trim().toLowerCase();
const isSupportedSort = (value: string | null): value is ServiceDiscoverySort =>
  value === 'name' || value === 'reviews' || value === 'rating';
const isSupportedIntent = (value: string | null): value is ServiceCapabilityIntent =>
  value === 'N' || value === 'U' || value === 'D';
const parsePositiveNumberParam = (value: string | null): number | null => {
  const normalized = String(value || '')
    .trim()
    .replace(',', '.');
  if (!normalized) return null;

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
};
const isExplicitFalseParam = (value: string | null): boolean => {
  const normalized = normalize(value || '');
  return normalized === '0' || normalized === 'false';
};
const isExplicitTrueParam = (value: string | null): boolean => {
  const normalized = normalize(value || '');
  return normalized === '1' || normalized === 'true';
};

const getIncludeUnpricedParamMode = (
  hourlyRateMax: number | null,
  includeUnpricedParam: string | null
): ServiceDiscoveryIncludeUnpricedParamMode => {
  if (hourlyRateMax === null) {
    return 'implicit';
  }
  if (isExplicitFalseParam(includeUnpricedParam)) {
    return 'explicit-off';
  }
  if (isExplicitTrueParam(includeUnpricedParam)) {
    return 'explicit-on';
  }
  return 'implicit';
};

export const getDiscoverySortFromParam = (value: string | null): ServiceDiscoverySort =>
  isSupportedSort(value) ? value : DEFAULT_DISCOVERY_SORT;

export const hasActiveHourlyRateFilter = (
  hourlyRateMax: ServiceDiscoveryFilters['hourlyRateMax']
): hourlyRateMax is number => typeof hourlyRateMax === 'number' && Number.isFinite(hourlyRateMax);

export const getServiceDiscoveryFiltersFromSearchParams = (
  searchParams: URLSearchParams
): ServiceDiscoveryFilters => {
  const hourlyRateMax = parsePositiveNumberParam(searchParams.get('hourlyRateMax'));
  const smartSelection = getSmartSearchSelectionByParams(searchParams);
  const intentParam = searchParams.get('intent');
  const issueType =
    intentParam === 'all'
      ? null
      : isSupportedIntent(intentParam)
        ? intentParam
        : smartSelection?.intent || null;
  const includeUnpricedParamMode = getIncludeUnpricedParamMode(
    hourlyRateMax,
    searchParams.get('includeUnpriced')
  );

  return {
    query: searchParams.get('q') || '',
    city: searchParams.get('city') || '',
    sortBy: getDiscoverySortFromParam(searchParams.get('sort')),
    issueType,
    smartSelection,
    hourlyRateMax,
    includeUnpriced:
      hourlyRateMax === null
        ? DEFAULT_DISCOVERY_INCLUDE_UNPRICED
        : includeUnpricedParamMode !== 'explicit-off',
    includeUnpricedParamMode,
  };
};

const setOrDeleteSearchParam = (searchParams: URLSearchParams, key: string, value: string) => {
  if (value) {
    searchParams.set(key, value);
  } else {
    searchParams.delete(key);
  }
};

const clearSmartSearchParams = (searchParams: URLSearchParams) => {
  searchParams.delete('smartEntryId');
  searchParams.delete('domainId');
  searchParams.delete('categoryId');
  searchParams.delete('objectId');
  searchParams.delete('matchType');
};

const setSmartSearchParams = (
  searchParams: URLSearchParams,
  smartSelection: SmartSearchSelection | null
) => {
  clearSmartSearchParams(searchParams);
  if (!smartSelection) return;

  searchParams.set('smartEntryId', smartSelection.providerId || smartSelection.id);
  searchParams.set('matchType', smartSelection.matchType);
  setOrDeleteSearchParam(searchParams, 'domainId', smartSelection.domainId || '');
  setOrDeleteSearchParam(searchParams, 'categoryId', smartSelection.categoryId || '');
  setOrDeleteSearchParam(searchParams, 'objectId', smartSelection.objectId || '');
};

export const buildServiceDiscoverySearchParams = (
  currentSearchParams: URLSearchParams,
  filters: ServiceDiscoveryFilters
): URLSearchParams => {
  const nextSearchParams = new URLSearchParams(currentSearchParams);
  const query = filters.query.trim();
  const city = filters.city.trim();
  const hasHourlyRate = hasActiveHourlyRateFilter(filters.hourlyRateMax);
  const selectedIntent = filters.issueType;

  setOrDeleteSearchParam(nextSearchParams, 'q', query);
  setSmartSearchParams(nextSearchParams, filters.smartSelection);
  setOrDeleteSearchParam(
    nextSearchParams,
    'intent',
    selectedIntent ? selectedIntent : filters.smartSelection?.intent ? 'all' : ''
  );
  setOrDeleteSearchParam(nextSearchParams, 'city', city);
  setOrDeleteSearchParam(
    nextSearchParams,
    'sort',
    filters.sortBy === DEFAULT_DISCOVERY_SORT ? '' : filters.sortBy
  );
  setOrDeleteSearchParam(
    nextSearchParams,
    'hourlyRateMax',
    hasHourlyRate ? String(filters.hourlyRateMax) : ''
  );
  setOrDeleteSearchParam(
    nextSearchParams,
    'includeUnpriced',
    hasHourlyRate
      ? filters.includeUnpricedParamMode === 'explicit-off'
        ? '0'
        : filters.includeUnpricedParamMode === 'explicit-on'
          ? '1'
          : ''
      : ''
  );

  return nextSearchParams;
};

export const mergeServiceDiscoverySearchParams = (
  currentSearchParams: URLSearchParams,
  currentFilters: ServiceDiscoveryFilters,
  nextUpdate: ServiceDiscoverySearchParamUpdate
): URLSearchParams => {
  const nextFilters: ServiceDiscoveryFilters = {
    ...currentFilters,
    ...nextUpdate,
  };

  if (!hasActiveHourlyRateFilter(nextFilters.hourlyRateMax)) {
    nextFilters.hourlyRateMax = null;
    nextFilters.includeUnpriced = DEFAULT_DISCOVERY_INCLUDE_UNPRICED;
    nextFilters.includeUnpricedParamMode = 'implicit';
  }

  if (!nextFilters.smartSelection) {
    nextFilters.smartSelection = null;
  }

  return buildServiceDiscoverySearchParams(currentSearchParams, nextFilters);
};

export const getServiceCity = (service: ServiceLocationInfo): string => {
  const address = service.address || '';
  const segments = address.split(',').map((segment) => segment.trim());
  return segments[segments.length - 1] || '';
};

const getSearchableTokens = (service: ServiceLocationInfo): string[] =>
  [
    service.serviceName || '',
    service.description || '',
    service.address || '',
    ...(service.specializations || []),
    ...(service.offeredCategories || []),
  ].map(normalize);

const getServiceSpecializationTokens = (service: ServiceLocationInfo): string[] =>
  [...(service.specializations || []), ...(service.offeredCategories || [])].filter(Boolean);

const getSmartSelectionLabelTokens = (selection: SmartSearchSelection): string[] =>
  [
    selection.suggestionTitle,
    selection.inputSelectedLabel,
    selection.suggestionMeta,
    selection.objectId
      ? providerCapabilityObjectById.get(selection.objectId)?.label ||
        providerCapabilityObjectById.get(selection.objectId)?.pluralLabel
      : '',
    selection.categoryId ? providerCapabilityCategoryById.get(selection.categoryId)?.label : '',
  ].filter(Boolean) as string[];

const serviceHasLegacySmartCompatMatch = (
  service: ServiceLocationInfo,
  selection: SmartSearchSelection
): boolean => {
  const legacyTokens = getSearchableTokens(service);
  const selectionTokens = getSmartSelectionLabelTokens(selection).map(normalize);

  return selectionTokens.some(
    (selectionToken) =>
      selectionToken.length >= 3 &&
      legacyTokens.some(
        (legacyToken) =>
          legacyToken.includes(selectionToken) || selectionToken.includes(legacyToken)
      )
  );
};

const itemSupportsIntent = (
  item: NonNullable<ServiceLocationInfo['serviceCapabilities']>['items'][number],
  intent: ServiceCapabilityIntent | null
): boolean => {
  if (!intent) return true;
  return (
    item.intents.includes(intent) ||
    item.objectSelections.some((selection) => selection.intents?.includes(intent))
  );
};

const objectSelectionSupportsIntent = (
  item: NonNullable<ServiceLocationInfo['serviceCapabilities']>['items'][number],
  objectId: string,
  intent: ServiceCapabilityIntent | null
): boolean => {
  const selection = item.objectSelections.find(
    (objectSelection) => objectSelection.objectId === objectId
  );
  if (!selection) return false;
  if (!intent) return true;
  return (selection.intents || item.intents).includes(intent);
};

export interface ServiceSmartMatch {
  matches: boolean;
  score: number;
  reason: string | null;
  usedLegacyCompat: boolean;
}

const buildMatchReason = (
  selection: SmartSearchSelection | null,
  issueType: ServiceDiscoveryIssueType,
  fallbackCategoryId?: string
): string | null => {
  const objectLabel = selection?.objectId
    ? providerCapabilityObjectById.get(selection.objectId)?.pluralLabel ||
      providerCapabilityObjectById.get(selection.objectId)?.label
    : null;
  const categoryLabel =
    objectLabel ||
    (selection?.categoryId
      ? providerCapabilityCategoryById.get(selection.categoryId)?.label
      : fallbackCategoryId
        ? providerCapabilityCategoryById.get(fallbackCategoryId)?.label
        : null);
  const intentLabel = issueType ? formatServiceCapabilityIntents([issueType]) : null;

  if (categoryLabel && intentLabel) return `${categoryLabel} · ${intentLabel}`;
  if (categoryLabel) return categoryLabel;
  if (intentLabel) return intentLabel;
  return null;
};

export const getServiceSmartMatch = (
  service: ServiceLocationInfo,
  smartSelection: SmartSearchSelection | null,
  issueType: ServiceDiscoveryIssueType
): ServiceSmartMatch => {
  if (smartSelection?.matchType === 'reportMissing') {
    return { matches: false, score: 0, reason: null, usedLegacyCompat: false };
  }

  if (smartSelection?.matchType === 'provider') {
    const providerId = smartSelection.providerId || smartSelection.id;
    const matches =
      service.uid === providerId ||
      normalize(service.serviceName) === normalize(smartSelection.inputSelectedLabel);
    return {
      matches,
      score: matches ? 1000 : 0,
      reason: matches ? 'Wybrany specjalista' : null,
      usedLegacyCompat: false,
    };
  }

  const capabilities = service.serviceCapabilities;
  const effectiveIntent = issueType;

  if (!smartSelection && !effectiveIntent) {
    return { matches: true, score: 0, reason: null, usedLegacyCompat: false };
  }

  if (!capabilities?.items?.length) {
    if (smartSelection && serviceHasLegacySmartCompatMatch(service, smartSelection)) {
      return {
        matches: !effectiveIntent || serviceHasLegacySmartCompatMatch(service, smartSelection),
        score: 10,
        reason: buildMatchReason(smartSelection, effectiveIntent),
        usedLegacyCompat: true,
      };
    }

    return { matches: false, score: 0, reason: null, usedLegacyCompat: false };
  }

  let bestScore = 0;
  let bestCategoryId: string | undefined;

  for (const item of capabilities.items) {
    if (!itemSupportsIntent(item, effectiveIntent)) continue;

    let score = 0;

    if (!smartSelection) {
      score = effectiveIntent ? 40 : 1;
    } else if (smartSelection.categoryId && item.categoryId !== smartSelection.categoryId) {
      score = 0;
    } else if (smartSelection.matchType === 'category') {
      if (item.mode === 'categoryWide') {
        score = 74;
      } else if (item.objectSelections.length > 0) {
        score = 62;
      }
    } else if (smartSelection.objectId) {
      if (item.mode === 'categoryWide') {
        score = item.excludedObjectIds.includes(smartSelection.objectId) ? 0 : 86;
      } else if (objectSelectionSupportsIntent(item, smartSelection.objectId, effectiveIntent)) {
        score = 112;
      }
    }

    if (score > 0 && smartSelection?.linkedCapabilityIds?.length) {
      const advancedBoost = smartSelection.linkedCapabilityIds.some((capabilityId) =>
        item.advancedCapabilityIds.includes(capabilityId)
      );
      if (advancedBoost) {
        score += 8;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestCategoryId = item.categoryId;
    }
  }

  return {
    matches: bestScore > 0,
    score: bestScore,
    reason: buildMatchReason(smartSelection, effectiveIntent, bestCategoryId),
    usedLegacyCompat: false,
  };
};

export const getBestMatchingSpecialization = (
  service: ServiceLocationInfo,
  rawQuery: string
): string | null => {
  const query = normalize(rawQuery);
  if (!query) return null;

  const candidates = getServiceSpecializationTokens(service);
  if (candidates.length === 0) return null;

  const exact = candidates.find((candidate) => normalize(candidate) === query);
  if (exact) return exact;

  const included = candidates.find((candidate) => normalize(candidate).includes(query));
  if (included) return included;

  const reverseIncluded = candidates.find((candidate) => query.includes(normalize(candidate)));
  if (reverseIncluded) return reverseIncluded;

  return null;
};

export const filterServices = (
  services: ServiceLocationInfo[],
  filters: Pick<
    ServiceDiscoveryFilters,
    'query' | 'city' | 'hourlyRateMax' | 'includeUnpriced' | 'issueType' | 'smartSelection'
  >
): ServiceLocationInfo[] => {
  const query = normalize(filters.query);
  const city = normalize(filters.city);
  const hourlyRateMax = hasActiveHourlyRateFilter(filters.hourlyRateMax)
    ? filters.hourlyRateMax
    : null;

  return services.filter((service) => {
    const matchesCity = !city || normalize(getServiceCity(service)) === city;
    if (!matchesCity) return false;

    if (hourlyRateMax !== null) {
      const publicHourlyRate = getPublicServiceHourlyRate(service);
      if (publicHourlyRate) {
        if (publicHourlyRate.amount > hourlyRateMax) {
          return false;
        }
      } else if (!filters.includeUnpriced) {
        return false;
      }
    }

    const smartMatch = getServiceSmartMatch(service, filters.smartSelection, filters.issueType);
    if (filters.smartSelection || filters.issueType) {
      return smartMatch.matches;
    }

    if (!query) return true;
    const tokens = getSearchableTokens(service);
    return tokens.some((token) => token.includes(query));
  });
};

export const sortServices = (
  services: ServiceLocationInfo[],
  sortBy: ServiceDiscoverySort,
  smartSelection: SmartSearchSelection | null = null,
  issueType: ServiceDiscoveryIssueType = null
): ServiceLocationInfo[] =>
  [...services].sort((a, b) => {
    if (smartSelection || issueType) {
      const smartScoreDiff =
        getServiceSmartMatch(b, smartSelection, issueType).score -
        getServiceSmartMatch(a, smartSelection, issueType).score;
      if (smartScoreDiff !== 0) return smartScoreDiff;
    }

    const reviewsCountA = a.reviewsCount ?? a.numberOfRatings ?? 0;
    const reviewsCountB = b.reviewsCount ?? b.numberOfRatings ?? 0;

    if (sortBy === 'rating') {
      return (b.averageRating || 0) - (a.averageRating || 0);
    }
    if (sortBy === 'reviews') {
      return reviewsCountB - reviewsCountA;
    }
    return (a.serviceName || '').localeCompare(b.serviceName || '', 'pl');
  });

export const getUniqueCities = (services: ServiceLocationInfo[]): string[] => {
  const cities = new Set<string>();
  services.forEach((service) => {
    const city = getServiceCity(service);
    if (city) cities.add(city);
  });
  return Array.from(cities).sort((a, b) => a.localeCompare(b, 'pl'));
};

export const formatHourlyRateFilterLabel = (hourlyRateMax: number): string =>
  `do ${formatServiceHourlyRateAmount(hourlyRateMax)} zł/h`;

const isPaucalPolishCount = (count: number): boolean => {
  const absoluteCount = Math.abs(count);
  const lastDigit = absoluteCount % 10;
  const lastTwoDigits = absoluteCount % 100;

  return lastDigit >= 2 && lastDigit <= 4 && !(lastTwoDigits >= 12 && lastTwoDigits <= 14);
};

export const getServiceDiscoveryCountNoun = (count: number): string => {
  if (count === 1) {
    return 'serwis';
  }

  return isPaucalPolishCount(count) ? 'serwisy' : 'serwisów';
};

export const formatServiceDiscoveryCountLabel = (count: number): string =>
  `${count} ${getServiceDiscoveryCountNoun(count)}`;

export const formatDiscoveryIssueTypeLabel = (issueType: ServiceCapabilityIntent | null): string =>
  issueType ? formatServiceCapabilityIntents([issueType]) : 'Wszystkie';

export const getServiceSmartMatchReason = (
  service: ServiceLocationInfo,
  smartSelection: SmartSearchSelection | null,
  issueType: ServiceDiscoveryIssueType
): string | null => getServiceSmartMatch(service, smartSelection, issueType).reason;

export const getDiscoverySmartQueryFallbackLabel = (
  selection: SmartSearchSelection | null
): string => selection?.inputSelectedLabel || '';
